import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, List, Users, ShieldAlert, RefreshCw, ArrowRightLeft, Network, Server, Search, Bell, HelpCircle, Zap, ChevronDown } from 'lucide-react';
import { cn, SystemBadge } from '../ui/Primitives';
const navGroups = [{
  label: 'OVERVIEW',
  items: [{
    icon: LayoutDashboard,
    label: 'Dashboard',
    path: '/'
  }, {
    icon: List,
    label: 'Event Log',
    path: '/events',
    badge: '12k today'
  }]
}, {
  label: 'DATA',
  items: [{
    icon: Users,
    label: 'Prospect Database',
    path: '/prospects'
  }, {
    icon: Server,
    label: 'Accounts',
    path: '/accounts'
  }]
}, {
  label: 'OPERATIONS',
  items: [{
    icon: ShieldAlert,
    label: 'Manual DNC',
    path: '/manual-dnc'
  }, {
    icon: RefreshCw,
    label: 'Refresh Jobs',
    path: '/refresh-jobs',
    badge: '3 active',
    badgeColor: 'text-electric-500 bg-electric-50'
  }, {
    icon: ArrowRightLeft,
    label: 'Reassignment',
    path: '/reassignment'
  }]
}, {
  label: 'INFRASTRUCTURE',
  items: [{
    icon: Network,
    label: 'Connections',
    path: '/connections'
  }, {
    icon: Zap,
    label: 'System Monitoring',
    path: '/monitoring'
  }]
}];
export function AppShell({
  children
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return 'Dashboard';
    const item = navGroups.flatMap(g => g.items).find(i => i.path === path);
    return item ? item.label : 'OptOut Sync';
  };
  return <div className="flex h-screen w-full bg-canvas overflow-hidden font-sans" data-id="element-22">
      {/* Sidebar */}
      <aside className="w-64 bg-navy-950 text-slate-300 flex flex-col flex-shrink-0 border-r border-navy-800" data-id="element-23">
        <div className="h-14 flex items-center px-4 border-b border-navy-800" data-id="element-24">
          <div className="flex items-center gap-2" data-id="element-25">
            <div className="bg-electric-500/20 p-1.5 rounded-md" data-id="element-26">
              <RefreshCw className="w-4 h-4 text-electric-400" data-id="element-27" />
            </div>
            <span className="font-semibold text-white tracking-tight" data-id="element-28">
              OptOut Sync
            </span>
          </div>
          <div className="ml-auto flex items-center gap-1.5 bg-navy-800 px-2 py-0.5 rounded-full text-[10px] font-medium text-emerald-400 border border-navy-700" data-id="element-29">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-30" />
            PROD
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-4 px-3 space-y-6" data-id="element-31">
          {navGroups.map((group, i) => <div key={i} data-id="element-32">
              <div className="px-3 mb-2 text-[10px] font-bold tracking-wider text-slate-500 uppercase" data-id="element-33">
                {group.label}
              </div>
              <div className="space-y-0.5" data-id="element-34">
                {group.items.map(item => <NavLink key={item.path} to={item.path} className={({
              isActive
            }) => cn('flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors', isActive ? 'bg-navy-800 text-white border-l-2 border-electric-500 pl-[10px]' : 'hover:bg-navy-900/50 hover:text-slate-200 border-l-2 border-transparent pl-[10px]')} data-id="element-35">
                    <item.icon className={cn('w-4 h-4', location.pathname === item.path ? 'text-electric-400' : 'text-slate-400')} data-id="element-36" />
                    {item.label}
                    {item.badge && <span className={cn('ml-auto text-[10px] px-1.5 py-0.5 rounded-md font-mono', item.badgeColor || 'bg-navy-800 text-slate-400')} data-id="element-37">
                        {item.badge}
                      </span>}
                  </NavLink>)}
              </div>
            </div>)}
        </div>

        <div className="p-4 border-t border-navy-800" data-id="element-38">
          <div className="flex items-center gap-3" data-id="element-39">
            <div className="w-8 h-8 rounded-full bg-navy-800 flex items-center justify-center text-sm font-medium text-white border border-navy-700" data-id="element-40">
              JD
            </div>
            <div className="flex flex-col" data-id="element-41">
              <span className="text-sm font-medium text-white" data-id="element-42">John Doe</span>
              <span className="text-xs text-slate-500" data-id="element-43">System Admin</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden" data-id="element-44">
        {/* Top Command Bar */}
        <header className="h-14 bg-surface border-b border-slate-200 flex items-center justify-between px-4 flex-shrink-0 z-10" data-id="element-45">
          <div className="flex items-center gap-4" data-id="element-46">
            <h1 className="text-sm font-semibold text-slate-800" data-id="element-47">
              {getPageTitle()}
            </h1>
          </div>

          <div className="flex items-center gap-3" data-id="element-48">
            <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50 border border-emerald-100 rounded-full" data-id="element-49">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-50" />
              <span className="text-xs font-medium text-emerald-700" data-id="element-51">
                All systems operational
              </span>
            </div>
            <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors relative" data-id="element-52">
              <Bell className="w-4 h-4" data-id="element-53" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white" data-id="element-54" />
            </button>
            <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors" data-id="element-55">
              <HelpCircle className="w-4 h-4" data-id="element-56" />
            </button>
          </div>
        </header>

        {/* System Status Strip */}
        <div className="h-8 bg-subtle border-b border-slate-200 flex items-center px-4 gap-6 text-xs flex-shrink-0" data-id="element-57">
          <span className="text-slate-500 font-medium" data-id="element-58">Live Status:</span>
          <div className="flex items-center gap-4" data-id="element-59">
            <SystemBadge system="bonzo" data-id="element-60" />
            <SystemBadge system="ghl" data-id="element-61" />
            <SystemBadge system="five9" data-id="element-62" />
            <SystemBadge system="salesforce" data-id="element-63" />
          </div>
          <span className="ml-auto text-slate-400 font-mono text-[10px]" data-id="element-64">
            Last checked: Just now
          </span>
        </div>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-65">{children}</div>
      </main>
    </div>;
}